import { differenceInMinutes } from "date-fns"
import { z } from "zod"

export const EXPIRATION_IN_MINUTES = {
  "15 minutes": 15,
  "30 minutes": 30,
  "1 hour": 60,
  "3 hours": 180,
  "6 hours": 360,
  "12 hours": 720,
  "1 day": 1440,
  "3 days": 4320,
  "7 days": 10_080,
  "30 days": 43_200,
  "60 days": 86_400,
  "6 months": 259_200,
  custom: "custom",
} as const

export type ExpirationOptions = keyof typeof EXPIRATION_IN_MINUTES

const expirationOptions = [
  "15 minutes",
  "30 minutes",
  "1 hour",
  "3 hours",
  "6 hours",
  "12 hours",
  "1 day",
  "3 days",
  "7 days",
  "30 days",
  "60 days",
  "6 months",
  "custom",
] as const satisfies ExpirationOptions[]

export const MINIMUM_EXPIRATION_IN_MINUTES = EXPIRATION_IN_MINUTES["15 minutes"]
export const MAXIMUM_EXPIRATION_IN_MINUTES = EXPIRATION_IN_MINUTES["6 months"]

export const expirationSchema = z
  .object({
    expirationType: z.enum(expirationOptions),
    customExpiration: z.date().optional(),
  })
  .superRefine(({ expirationType, customExpiration }, ctx) => {
    if (expirationType !== EXPIRATION_IN_MINUTES.custom) {
      return
    }

    if (!customExpiration) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expiration date is required.",
        path: ["expiration"],
      })

      return
    }

    if (
      differenceInMinutes(customExpiration, new Date()) <
      MINIMUM_EXPIRATION_IN_MINUTES
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expiration date must be at least 15 minutes from now.",
        path: ["customExpiration"],
      })
    }

    if (
      differenceInMinutes(customExpiration, new Date()) >
      MAXIMUM_EXPIRATION_IN_MINUTES
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expiration date must be less than 6 months from now.",
        path: ["customExpiration"],
      })
    }
  })
